// Redux
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

// Constants
import { LoadingStates, UNAUTHORISED } from "constants/global";
// API
import {
	rejectDeclaration as APIRejectDeclaration,
	acceptMonthlyDeclaration as APIAcceptMonthlyDeclaration,
	getMonthlyReimbSummaryData as APIGetReimbSummary,
	approveOrRejectClaim as APIApproveOrRejectClaim,
	getReimbDetailsMonthlyDeclarations as APIGetReimbDetailsMonthlyDeclarations,
	getReimClaimDetails as APIGetReimClaimDetails,
	approveReimbursement as APIApproveReimbursement,
	getVoucherDetailsReport as APIGetVoucherDetailsReport
} from "services/api/reimbursement";

// Utils
import { getReimbVoucherGroup } from "utils/Generic/generic";

// Type Definitions
import type { Reimbursement, RootState } from "redux/types";
import type {
	AcceptMonthlyDeclarationResponse,
	ApiErrorResponse,
	RejectDeclarationResponse,
	GetReimbSummaryResponse,
	ApproveOrRejectResponse,
	GetReimbDetailsResponseMonthlyDeclarations,
	ReimVerificationApprovalClaimDetails,
	GetVoucherDetailsReportPayload,
	GetVoucherDetailsReportResponse
} from "services/api/types";

export const initialState: Reimbursement = {
	loading: LoadingStates.DEFAULT,
	defaultFileList: [],
	declarationResponse: {},
	hasUnaurizedError: false,
	monthlyReimbursementSummary: {
		loading: LoadingStates.DEFAULT,
		reimbursementSummary: {}
	},
	reimbursementSummaryList: {
		loading: LoadingStates.DEFAULT
	},
	reimbursementSummaryListForAdmin: {
		loading: LoadingStates.DEFAULT,
		summaryList: {}
	},
	voucherGroup: {
		loading: LoadingStates.DEFAULT,
		groupList: {},
		recordCount: 0,
		groupCodeCount: {},
		summary: {}
	},
	verification: {
		verificationClaimDetails: undefined,
		loading: LoadingStates.DEFAULT,
		approveOrRejectClaimResponse: undefined
	},
	approval: {
		approvalClaimDetails: undefined,
		loading: LoadingStates.DEFAULT,
		approveOrRejectClaimResponse: undefined
	},
	reimbursementDetailsMonthlyDeclarations: {
		loading: LoadingStates.DEFAULT
	},
	selectedVoucherGroupCodeMonthlyDeclarations: {}
};

const setAuthTokenError = (draft: Reimbursement, error: any): void => {
	if (error?.return_description === UNAUTHORISED) {
		draft.hasUnaurizedError = true;
	}
};

export const rejectReimbursement = createAsyncThunk<RejectDeclarationResponse, FormData, { rejectValue: ApiErrorResponse; state: RootState }>(
	"REJECT_REIMBURSEMENT",
	async (payload, { rejectWithValue }) => {
		try {
			const { data } = await APIRejectDeclaration(payload);
			return data;
		} catch (error) {
			return rejectWithValue(error as ApiErrorResponse);
		}
	}
);

export const acceptReimbursement = createAsyncThunk<AcceptMonthlyDeclarationResponse, { reimb_summ_id: number }, { rejectValue: ApiErrorResponse; state: RootState }>(
	"ACCEPT_REIMBURSEMENT",
	async (payload, { rejectWithValue }) => {
		try {
			const { data } = await APIAcceptMonthlyDeclaration(payload);
			return data;
		} catch (error) {
			return rejectWithValue(error as ApiErrorResponse);
		}
	}
);

export const getReimbursementSummary = createAsyncThunk<GetReimbSummaryResponse, { reimb_summ_id: number }, { rejectValue: ApiErrorResponse; state: RootState }>(
	"REIMBURSEMENT_SUMMARY",
	async (payload, { rejectWithValue }) => {
		try {
			const { data } = await APIGetReimbSummary(payload);
			return data;
		} catch (error) {
			return rejectWithValue(error as ApiErrorResponse);
		}
	}
);
export const getReimbSummary = createAsyncThunk<
	GetReimbSummaryResponse,
	{
		page_number?: number;
		page_records?: number;
		from_year_month?: string;
		to_year_month?: string;
		voucher_group_code?: string;
		reimb_status?: string;
		outlet_code?: string;
	},
	{ rejectValue: ApiErrorResponse; state: RootState }
>("GET_REIMBURSEMENT_SUMMARY", async (payload, { rejectWithValue }) => {
	try {
		const { data } = await APIGetReimbSummary(payload);
		return { ...data, selectedVoucherCode: parseInt(payload.voucher_group_code ?? "-1") };
	} catch (error) {
		return rejectWithValue(error as ApiErrorResponse);
	}
});

export const getReimbDataForAdmin = createAsyncThunk<
	GetReimbSummaryResponse,
	{
		reqPayload: {
			page_number?: number;
			page_records?: number;
			from_year_month?: string;
			to_year_month?: string;
			voucher_group_code: string;
			reimb_status: string;
		};
		pathName: string;
	},
	{ rejectValue: ApiErrorResponse; state: RootState }
>("GET_REIMBURSEMENT_ADMIN", async (payload, { rejectWithValue }) => {
	try {
		const { data } = await APIGetReimbSummary(payload.reqPayload);
		return { ...data, pathName: payload.pathName, selectedVoucherCode: parseInt(payload.reqPayload.voucher_group_code) };
	} catch (error) {
		return rejectWithValue(error as ApiErrorResponse);
	}
});

const reimbClaimDetailsFunction = async (
	payload: {
		reimb_summ_id: string;
	},
	rejectWithValue: (value: ApiErrorResponse) => any
) => {
	try {
		const { data } = await APIGetReimClaimDetails(payload);
		return data;
	} catch (error) {
		return rejectWithValue(error as ApiErrorResponse);
	}
};

export const getReimbVerificationClaimDetails = createAsyncThunk<
	ReimVerificationApprovalClaimDetails,
	{
		reimb_summ_id: string;
	},
	{ rejectValue: ApiErrorResponse; state: RootState }
>("GET_REIMBURSEMENT_CLAIM_VERIFICATION_DETAILS", async (payload, { rejectWithValue }) => {
	return reimbClaimDetailsFunction(payload, rejectWithValue);
});
export const getReimbApprovalClaimDetails = createAsyncThunk<
	ReimVerificationApprovalClaimDetails,
	{
		reimb_summ_id: string;
	},
	{ rejectValue: ApiErrorResponse; state: RootState }
>("GET_REIMBURSEMENT_CLAIM_APPROVAL_DETAILS", async (payload, { rejectWithValue }) => {
	return reimbClaimDetailsFunction(payload, rejectWithValue);
});

export const getReimbVoucherGroupList = createAsyncThunk<
	GetReimbSummaryResponse,
	{
		reqPayload: {
			from_year_month?: string;
			to_year_month?: string;
			reimb_status?: string;
		};
		pathName: string;
	},
	{ rejectValue: ApiErrorResponse; state: RootState }
>("GET_REIMBURSEMENT_VOUCHER_GROUP_LIST", async (payload, { rejectWithValue }) => {
	try {
		const { data } = await APIGetReimbSummary(payload.reqPayload);
		return { ...data, pathName: payload.pathName };
	} catch (error) {
		return rejectWithValue(error as ApiErrorResponse);
	}
});

// Approve or reject reimbursement verification claim
export const rejectOrApproveVerificationClaim = createAsyncThunk<
	ApproveOrRejectResponse,
	{
		reimb_summ_id: number;
		reject_claim: string;
		remarks?: string;
		amount?: number;
		voucher_group_code?: string;
	},
	{ rejectValue: ApiErrorResponse; state: RootState }
>("REJECT_OR_APPROVE_REIMBURSEMENT", async (payload, { rejectWithValue }) => {
	try {
		const { data } = await APIApproveOrRejectClaim(payload);
		return data;
	} catch (error) {
		return rejectWithValue(error as ApiErrorResponse);
	}
});

// Approve  reimbursement approval claim
export const approveReimbursementClaim = createAsyncThunk<
	ApproveOrRejectResponse,
	{
		reimb_summ_id: number;
		reject_claim: string;
		remarks?: string;
		amount?: number;
		voucher_group_code?: string;
	},
	{ rejectValue: ApiErrorResponse; state: RootState }
>("APPROVE_REIMBURSEMENT", async (payload, { rejectWithValue }) => {
	try {
		const { data } = await APIApproveReimbursement(payload);
		return data;
	} catch (error) {
		return rejectWithValue(error as ApiErrorResponse);
	}
});

export const getReimbDetailsMonthlyDeclarations = createAsyncThunk<
	GetReimbDetailsResponseMonthlyDeclarations,
	{
		page_number?: number;
		page_records?: number;
		from_year_month: string;
		to_year_month: string;
		outlet_code?: string;
		voucher_group_code?: string;
	},
	{ rejectValue: ApiErrorResponse; state: RootState }
>("GET_REIMBURSEMENT_DETAILS", async (payload, { rejectWithValue }) => {
	try {
		const { data } = await APIGetReimbDetailsMonthlyDeclarations(payload);
		const uniqueCode = parseInt(`${payload.voucher_group_code}${payload.to_year_month}`);
		return { ...data, selectedVoucherCode: isNaN(uniqueCode) ? 0 : uniqueCode };
	} catch (error) {
		return rejectWithValue(error as ApiErrorResponse);
	}
});

export const getVoucherDetailsReport = createAsyncThunk<GetVoucherDetailsReportResponse, GetVoucherDetailsReportPayload, { rejectValue: ApiErrorResponse; state: RootState }>(
	"GET_VOUCHER_DETAILS_REPORT",
	async (payload, { rejectWithValue }) => {
		try {
			const { data } = await APIGetVoucherDetailsReport(payload);
			return data;
		} catch (error) {
			return rejectWithValue(error as ApiErrorResponse);
		}
	}
);

export const reimbursementSlice = createSlice({
	name: "REIMBURSEMENT",
	initialState,
	reducers: {
		resetReimbState: () => {
			return { ...initialState };
		},
		resetReimbursementDetailsMonthlyDeclarations: (state: Reimbursement) => {
			return {
				...state,
				reimbursementDetailsMonthlyDeclarations: initialState.reimbursementDetailsMonthlyDeclarations,
				selectedVoucherGroupCodeMonthlyDeclarations: initialState.selectedVoucherGroupCodeMonthlyDeclarations
			};
		},
		updateSelectedVoucherGroupCodeMonthlyDeclarations: (state: Reimbursement, { payload }) => {
			return { ...state, selectedVoucherGroupCodeMonthlyDeclarations: payload };
		},

		updateFileList: (state: Reimbursement, { payload }) => {
			return { ...state, defaultFileList: payload };
		},

		filterFileList: (state: Reimbursement, { payload }) => {
			const filteredValue = state.defaultFileList.filter((file) => file !== payload.url);
			return { ...state, defaultFileList: filteredValue };
		},
		resetVerificationClaimDetails: (state: Reimbursement) => {
			return {
				...state,
				verification: {
					...state?.verification,
					verificationClaimDetails: undefined
				}
			};
		}
	},
	extraReducers: (builder) => {
		// Reject Monthly Reimbursement Declaration
		builder.addCase(rejectReimbursement.pending, (draft: Reimbursement) => {
			draft.loading = LoadingStates.LOADING;
		});

		builder.addCase(rejectReimbursement.fulfilled, (draft: Reimbursement, { payload }) => {
			draft.declarationResponse = payload;
			draft.loading = LoadingStates.IDLE;
		});
		builder.addCase(rejectReimbursement.rejected, (state, { payload }) => {
			state.loading = LoadingStates.IDLE;
			setAuthTokenError(state, payload);
		});

		// Accept Declaration
		builder.addCase(acceptReimbursement.pending, (draft: Reimbursement) => {
			draft.loading = LoadingStates.LOADING;
		});

		builder.addCase(acceptReimbursement.fulfilled, (draft: Reimbursement, { payload }) => {
			draft.declarationResponse = payload;
			draft.loading = LoadingStates.IDLE;
		});
		builder.addCase(acceptReimbursement.rejected, (state, { payload }) => {
			state.loading = LoadingStates.IDLE;
			setAuthTokenError(state, payload);
		});

		// Reimbursement Summary
		builder.addCase(getReimbursementSummary.pending, (draft: Reimbursement) => {
			draft.monthlyReimbursementSummary.loading = LoadingStates.LOADING;
		});

		builder.addCase(getReimbursementSummary.fulfilled, (draft: Reimbursement, { payload }) => {
			draft.monthlyReimbursementSummary.reimbursementSummary = payload;
			draft.monthlyReimbursementSummary.loading = LoadingStates.IDLE;
		});
		builder.addCase(getReimbursementSummary.rejected, (state, { payload }) => {
			state.monthlyReimbursementSummary.loading = LoadingStates.IDLE;
			setAuthTokenError(state, payload);
		});
		// get Reimbursement Summary
		builder.addCase(getReimbSummary.pending, (draft: Reimbursement) => {
			draft.reimbursementSummaryList.loading = LoadingStates.LOADING;
		});

		builder.addCase(getReimbSummary.fulfilled, (draft: Reimbursement, { payload }) => {
			draft.reimbursementSummaryList[payload?.selectedVoucherCode] = payload.data;
			draft.reimbursementSummaryList.loading = LoadingStates.IDLE;
		});
		builder.addCase(getReimbSummary.rejected, (state, { payload }) => {
			state.reimbursementSummaryList.loading = LoadingStates.IDLE;
			setAuthTokenError(state, payload);
		});

		// get Reimbursement Summary for Admin
		builder.addCase(getReimbDataForAdmin.pending, (draft: Reimbursement) => {
			draft.reimbursementSummaryListForAdmin.loading = LoadingStates.LOADING;
		});

		builder.addCase(getReimbDataForAdmin.fulfilled, (draft: Reimbursement, { payload }) => {
			draft.reimbursementSummaryListForAdmin.summaryList[payload?.pathName + payload?.selectedVoucherCode] = payload.data;
			draft.reimbursementSummaryListForAdmin.loading = LoadingStates.IDLE;
		});
		builder.addCase(getReimbDataForAdmin.rejected, (state, { payload }) => {
			state.reimbursementSummaryListForAdmin.loading = LoadingStates.IDLE;
			setAuthTokenError(state, payload);
		});

		// get Reimbursement Voucher Group list
		builder.addCase(getReimbVoucherGroupList.pending, (draft: Reimbursement) => {
			draft.voucherGroup.loading = LoadingStates.LOADING;
		});

		builder.addCase(getReimbVoucherGroupList.fulfilled, (draft: Reimbursement, { payload }) => {
			draft.voucherGroup.recordCount = payload.data.record_count;
			draft.voucherGroup.groupCodeCount = payload.data.group_code_count;
			draft.voucherGroup.groupList[payload?.pathName] = getReimbVoucherGroup(payload);
			draft.voucherGroup.summary[payload?.pathName] = payload?.data?.reimb_summ;
			draft.voucherGroup.loading = LoadingStates.IDLE;
		});
		builder.addCase(getReimbVoucherGroupList.rejected, (state, { payload }) => {
			state.voucherGroup.loading = LoadingStates.IDLE;
			setAuthTokenError(state, payload);
		});

		// Approve or reject reimbursement verification claim
		builder.addCase(rejectOrApproveVerificationClaim.pending, (draft: Reimbursement) => {
			draft.verification.loading = LoadingStates.LOADING;
		});
		builder.addCase(rejectOrApproveVerificationClaim.fulfilled, (draft: Reimbursement, { payload }) => {
			draft.verification.approveOrRejectClaimResponse = payload;
			draft.verification.loading = LoadingStates.IDLE;
		});
		builder.addCase(rejectOrApproveVerificationClaim.rejected, (state, { payload }) => {
			state.verification.loading = LoadingStates.IDLE;
			setAuthTokenError(state, payload);
		});

		// Approve reimbursement approval claim
		builder.addCase(approveReimbursementClaim.pending, (draft: Reimbursement) => {
			draft.approval.loading = LoadingStates.LOADING;
		});
		builder.addCase(approveReimbursementClaim.fulfilled, (draft: Reimbursement, { payload }) => {
			draft.approval.approveOrRejectClaimResponse = payload;
			draft.approval.loading = LoadingStates.IDLE;
		});
		builder.addCase(approveReimbursementClaim.rejected, (state, { payload }) => {
			state.approval.loading = LoadingStates.IDLE;
			setAuthTokenError(state, payload);
		});

		// Get Reimbursement Details for Monthly Declarations
		builder.addCase(getReimbDetailsMonthlyDeclarations.pending, (draft: Reimbursement) => {
			draft.reimbursementDetailsMonthlyDeclarations.loading = LoadingStates.LOADING;
		});

		builder.addCase(getReimbDetailsMonthlyDeclarations.fulfilled, (draft: Reimbursement, { payload }) => {
			draft.reimbursementDetailsMonthlyDeclarations.loading = LoadingStates.IDLE;
			draft.reimbursementDetailsMonthlyDeclarations[payload?.selectedVoucherCode] = payload.data;
		});

		builder.addCase(getReimbDetailsMonthlyDeclarations.rejected, (draft: Reimbursement, { payload }) => {
			draft.reimbursementDetailsMonthlyDeclarations.loading = LoadingStates.IDLE;
			setAuthTokenError(draft, payload);
		});

		// Get REIM verification claim details
		builder.addCase(getReimbVerificationClaimDetails.pending, (draft: Reimbursement) => {
			draft.verification.loading = LoadingStates.LOADING;
		});
		builder.addCase(getReimbVerificationClaimDetails.fulfilled, (draft: Reimbursement, { payload }) => {
			draft.verification.verificationClaimDetails = payload;
			draft.verification.loading = LoadingStates.IDLE;
		});
		builder.addCase(getReimbVerificationClaimDetails.rejected, (state, { payload }) => {
			state.verification.loading = LoadingStates.IDLE;
			setAuthTokenError(state, payload);
		});

		// Get REIM approval claim details
		builder.addCase(getReimbApprovalClaimDetails.pending, (draft: Reimbursement) => {
			draft.approval.loading = LoadingStates.LOADING;
		});
		builder.addCase(getReimbApprovalClaimDetails.fulfilled, (draft: Reimbursement, { payload }) => {
			draft.approval.approvalClaimDetails = payload;
			draft.approval.loading = LoadingStates.IDLE;
		});
		builder.addCase(getReimbApprovalClaimDetails.rejected, (state, { payload }) => {
			state.approval.loading = LoadingStates.IDLE;
			setAuthTokenError(state, payload);
		});

		// GET Voucher Details Report
		builder.addCase(getVoucherDetailsReport.pending, (draft: Reimbursement) => {
			draft.loading = LoadingStates.LOADING;
		});

		builder.addCase(getVoucherDetailsReport.fulfilled, (draft: Reimbursement) => {
			draft.loading = LoadingStates.IDLE;
		});
		builder.addCase(getVoucherDetailsReport.rejected, (state, { payload }) => {
			state.loading = LoadingStates.IDLE;
			setAuthTokenError(state, payload);
		});
	}
});

// Selectors
export const getReimbursementSummarySelector = (state: RootState) => state?.reimbursement?.monthlyReimbursementSummary?.reimbursementSummary;
export const getReimbursementSummaryLoadingSelector = (state: RootState) => state?.reimbursement?.monthlyReimbursementSummary?.loading;
export const reimbSummaryListSelector = (state: RootState) => state?.reimbursement?.reimbursementSummaryList;
export const reimbDetailsSelectorMonthlyDeclarations = (state: RootState) => state?.reimbursement?.reimbursementDetailsMonthlyDeclarations;

export const getLoadingStateForReimbSummaryList = (state: RootState): LoadingStates => state?.reimbursement?.reimbursementSummaryList.loading;
export const getLoadingStateForReimbDetailsMonthlyDeclarations = (state: RootState): LoadingStates => state?.reimbursement?.reimbursementDetailsMonthlyDeclarations.loading;
export const getLoadingSelector = (state: RootState): LoadingStates => state?.reimbursement?.loading;

export const selectedVoucherGroupCode = (state: RootState) => state?.reimbursement?.selectedVoucherGroupCodeMonthlyDeclarations;

export const reimbSummaryListForAdminSelector = (state: RootState) => state?.reimbursement?.reimbursementSummaryListForAdmin;

export const reimbVoucherGroupListSelector = (state: RootState) => state?.reimbursement?.voucherGroup;
export const loadingVoucherGroups = (state: RootState) => state?.reimbursement?.voucherGroup.loading;

export const getLoadingStateForReimbSummaryListForAdmin = (state: RootState): LoadingStates => state?.reimbursement?.reimbursementSummaryListForAdmin.loading;

// getFiles Selector
export const getFilesSelector = (state: RootState) => state?.reimbursement?.defaultFileList;

// Get verification claim details
export const verificationClaimDetailsSelector = (state: RootState) => state?.reimbursement?.verification?.verificationClaimDetails;
export const verificationClaimLoadingSelector = (state: RootState) => state?.reimbursement?.verification?.loading;

// Get approval claim details
export const approvalClaimDetailsSelector = (state: RootState) => state?.reimbursement?.approval?.approvalClaimDetails;
export const approvalClaimLoadingSelector = (state: RootState) => state?.reimbursement?.approval?.loading;
export const hasTokenErrReimbSelector = (state: RootState) => state?.reimbursement?.hasUnaurizedError;

// Actions
export const {
	resetVerificationClaimDetails,
	resetReimbState,
	filterFileList,
	resetReimbursementDetailsMonthlyDeclarations,
	updateFileList,
	updateSelectedVoucherGroupCodeMonthlyDeclarations
} = reimbursementSlice.actions;

// Reducers
export default reimbursementSlice.reducer;
